import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, FormControl, FormLabel,
  RadioGroup, FormControlLabel, Radio, Select, MenuItem, TextField,
  Checkbox, FormGroup, LinearProgress, Chip, Grid, InputLabel, Avatar,
  List, ListItem, ListItemText, Divider, Alert, Snackbar, Autocomplete,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Apartment as ApartmentIcon, Home as HomeIcon, Person as PersonIcon,
  History as HistoryIcon, GetApp as ExportIcon, Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { exportRequestAPI, exportHelpers } from './api';
import { useAppTheme } from '../../contexts/ThemeContext';

// ── Export Card ──────────────────────────────────────────────────────────────

const ExportCard = ({ title, icon, color, onExport, isExporting, progress, isDisabled = false, children }) => {
  const { palette } = useAppTheme();
  return (
    <Card elevation={0} sx={{
      height: '100%', transition: 'all 0.3s ease',
      backgroundColor: palette.background.secondary,
      border: `1px solid ${palette.border}`,
      opacity: isDisabled ? 0.7 : 1,
      '&:hover': { transform: isDisabled ? 'none' : 'translateY(-4px)', boxShadow: isDisabled ? 1 : 4 }
    }}>
      <Box sx={{ borderTop: `4px solid ${color}` }} />
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ bgcolor: color, mr: 2, width: 50, height: 50 }}>{icon}</Avatar>
          <Typography variant="h6" fontWeight={600} sx={{ color: palette.text.primary }}>{title}</Typography>
        </Box>

        {children}

        <Button variant="contained" fullWidth disabled={isExporting || isDisabled} onClick={onExport}
          startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <ExportIcon />}
          sx={{ mt: 2, py: 1.5, bgcolor: color, '&:hover': { bgcolor: color, filter: 'brightness(0.9)' }, textTransform: 'none', fontWeight: 600 }}>
          {isExporting ? 'Exporting...' : `Export ${title}`}
        </Button>

        {isExporting && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress}
              sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${color}, #10b981, #34d399)` } }} />
            <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: palette.text.secondary }}>
              {Math.round(progress)}% Complete
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ── Export History ────────────────────────────────────────────────────────────

const ExportHistory = ({ history, onDownload, onRefresh, loading }) => {
  const { palette, Colors } = useAppTheme();
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':  return 'success';
      case 'processing': return 'warning';
      case 'pending':    return 'info';
      case 'failed':     return 'error';
      default:           return 'default';
    }
  };
  return (
    <Card elevation={0} sx={{ backgroundColor: palette.background.secondary, border: `1px solid ${palette.border}` }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: Colors.accent.purple, mr: 2, width: 40, height: 40 }}><HistoryIcon /></Avatar>
            <Typography variant="h6" fontWeight={600} sx={{ color: palette.text.primary }}>Recent Exports</Typography>
          </Box>
          <Button startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={onRefresh} disabled={loading} size="small"
            sx={{ color: Colors.primary.main }}>
            Refresh
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: Colors.primary.main }} /></Box>
        ) : history.length === 0 ? (
          <Typography variant="body2" sx={{ color: palette.text.muted, textAlign: 'center', py: 4 }}>No export history found</Typography>
        ) : (
          <List sx={{ p: 0 }}>
            {history.map((item, index) => (
              <React.Fragment key={item.id || index}>
                <ListItem sx={{
                  px: 2, py: 1.5, borderRadius: 1, mb: 1,
                  backgroundColor: palette.background.tertiary,
                  transition: 'all 0.3s ease',
                  '&:hover': { backgroundColor: `${Colors.primary.main}10`, transform: 'translateX(4px)' }
                }}>
                  <ListItemText
                    primary={<Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.text.primary }}>{item.export_type_display || item.title}</Typography>}
                    secondary={
                      <Box>
                        <Typography variant="caption" sx={{ color: palette.text.secondary }}>
                          {item.created_at ? new Date(item.created_at).toLocaleString() : item.date}
                        </Typography>
                        {item.file_name && (
                          <Typography variant="caption" sx={{ color: palette.text.muted, display: 'block' }}>File: {item.file_name}</Typography>
                        )}
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={item.export_status || item.status} size="small" color={getStatusColor(item.export_status || item.status)}
                      variant="filled" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }} />
                    {(item.export_status || item.status) === 'completed' && (
                      <Button size="small" startIcon={<DownloadIcon />} onClick={() => onDownload(item.id)}
                        sx={{ minWidth: 'auto', color: Colors.primary.main }}>Download</Button>
                    )}
                  </Box>
                </ListItem>
                {index < history.length - 1 && <Divider sx={{ my: 0.5, borderColor: palette.border }} />}
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

// ── Progress Dialog ───────────────────────────────────────────────────────────

const ProgressDialog = ({ open, onClose, exportType, progress, status, error }) => {
  const { palette, Colors } = useAppTheme();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: palette.background.secondary, color: palette.text.primary } }}>
      <DialogTitle sx={{ color: palette.text.primary }}>Exporting {exportType} Data</DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <LinearProgress variant="determinate" value={progress}
            sx={{ mb: 2, height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: Colors.primary.main } }} />
          <Typography variant="body2" sx={{ color: palette.text.secondary }}>Status: {status}</Typography>
          <Typography variant="body2" sx={{ color: palette.text.secondary }}>Progress: {Math.round(progress)}%</Typography>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={status === 'processing'} sx={{ color: palette.text.secondary }}>
          {status === 'completed' ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const ExportCenter = () => {
  const { palette, Colors } = useAppTheme();

  const [exportStates, setExportStates] = useState({
    apartment: { isExporting: false, progress: 0, status: '', error: null },
    category:  { isExporting: false, progress: 0, status: '', error: null },
    tenant:    { isExporting: false, progress: 0, status: '', error: null },
  });

  const [formData, setFormData] = useState({
    apartmentSelection: 'all', apartmentPeriod: 'current', selectedApartments: [],
    apartmentMonth: new Date().toISOString().slice(0, 7),
    apartmentDateFrom: new Date(Date.now() - 30*86400000).toISOString().split('T')[0],
    apartmentDateTo: new Date().toISOString().split('T')[0],
    apartmentExportTenantsBased: false,
    categoryApartment: '', categoryType: '', categoryPeriod: 'current',
    categoryMonth: new Date().toISOString().slice(0, 7),
    categoryDateFrom: new Date(Date.now() - 30*86400000).toISOString().split('T')[0],
    categoryDateTo: new Date().toISOString().split('T')[0],
    categoryExportTenantsBased: false,
    tenantSearch: '', selectedTenant: '', tenantSelection: 'specific',
    tenantApartmentSelection: 'all', tenantSelectedApartments: [],
    tenantDateFrom: new Date(Date.now() - 30*86400000).toISOString().split('T')[0],
    tenantDateTo: new Date().toISOString().split('T')[0],
  });

  const [apartments,     setApartments]     = useState([]);
  const [categories,     setCategories]     = useState([]);
  const [tenants,        setTenants]        = useState([]);
  const [exportHistory,  setExportHistory]  = useState([]);
  const [loading,        setLoading]        = useState({ apartments: false, categories: false, tenants: false, history: false });
  const [notification,   setNotification]   = useState({ open: false, message: '', severity: 'success' });
  const [progressDialog, setProgressDialog] = useState({ open: false, type: '', progress: 0, status: '', error: null });

  useEffect(() => { loadApartments(); loadExportHistory(); }, []);
  useEffect(() => { if (formData.categoryApartment) loadCategories(formData.categoryApartment); }, [formData.categoryApartment]);
  useEffect(() => { if (formData.tenantSelection === 'specific' && tenants.length === 0) loadTenants(); }, [formData.tenantSelection]);

  const setLoad  = (key, val) => setLoading(p => ({ ...p, [key]: val }));
  const showNote = (message, severity = 'info') => setNotification({ open: true, message, severity });
  const handleFormChange = (field, value) => setFormData(p => ({ ...p, [field]: value }));

  const loadApartments = async () => {
    setLoad('apartments', true);
    try {
      const r = await exportRequestAPI.getApartments();
      if (r.success) setApartments(r.data); else showNote('Failed to load apartments', 'error');
    } catch { showNote('Error loading apartments', 'error'); }
    setLoad('apartments', false);
  };

  const loadCategories = async (aptId) => {
    setLoad('categories', true);
    try {
      const r = await exportRequestAPI.getCategories(aptId);
      if (r.success) setCategories(r.data); else showNote('Failed to load categories', 'error');
    } catch { showNote('Error loading categories', 'error'); }
    setLoad('categories', false);
  };

  const loadTenants = async (search = '') => {
    setLoad('tenants', true);
    try {
      const r = await exportRequestAPI.getTenants(search);
      if (r.success) setTenants(r.data); else showNote('Failed to load tenants', 'error');
    } catch { showNote('Error loading tenants', 'error'); }
    setLoad('tenants', false);
  };

  const loadExportHistory = async () => {
    setLoad('history', true);
    try {
      const r = await exportRequestAPI.list({ page_size: 10 });
      if (r.success) setExportHistory(r.data.results || r.data); else showNote('Failed to load export history', 'error');
    } catch { showNote('Error loading export history', 'error'); }
    setLoad('history', false);
  };

  const handleApartmentCheckbox = (id, checked) => {
    setFormData(p => ({ ...p, selectedApartments: checked ? [...p.selectedApartments, id] : p.selectedApartments.filter(x => x !== id) }));
  };

  const runExport = async (type, params, helperFn) => {
    setExportStates(p => ({ ...p, [type]: { isExporting: true, progress: 0, status: 'starting', error: null } }));
    try {
      const result = await helperFn(params);
      if (result.success) {
        showNote('Export started successfully', 'success');
        await exportHelpers.pollProgress(result.data.id, (prog) => {
          setExportStates(p => ({ ...p, [type]: { isExporting: prog.status !== 'completed', progress: prog.progress || 0, status: prog.status, error: prog.error } }));
        });
        showNote('Export completed successfully', 'success');
        loadExportHistory();
      } else throw new Error(result.error?.message || result.error || 'Export failed');
    } catch (err) {
      setExportStates(p => ({ ...p, [type]: { isExporting: false, progress: 0, status: 'failed', error: err.message } }));
      showNote(`Export failed: ${err.message || err}`, 'error');
    }
  };

  const handleApartmentExport = () => runExport('apartment', {
    exportType: formData.apartmentExportTenantsBased ? 'tenants_based' : 'apartment',
    periodType: formData.apartmentPeriod, apartmentSelection: formData.apartmentSelection,
    selectedApartments: formData.selectedApartments, monthYear: formData.apartmentMonth,
    dateFrom: formData.apartmentDateFrom, dateTo: formData.apartmentDateTo
  }, exportHelpers.exportApartmentData);

  const handleCategoryExport = () => runExport('category', {
    exportType: formData.categoryExportTenantsBased ? 'tenants_based' : 'category',
    categoryApartment: formData.categoryApartment, categoryType: formData.categoryType,
    selectedCategoryId: formData.categoryType, dateFrom: formData.categoryDateFrom, dateTo: formData.categoryDateTo,
    includePayments: true, includeTenantDetails: true
  }, exportHelpers.exportCategoryData);

  const handleTenantExport = () => {
    const params = { export_type: 'tenant', date_from: formData.tenantDateFrom, date_to: formData.tenantDateTo };
    if (formData.tenantSelection === 'specific') {
      if (!formData.selectedTenant) { showNote('Please select a specific tenant', 'error'); return; }
      params.selected_tenant_id = formData.selectedTenant;
    } else {
      params.apartment_selection = formData.tenantApartmentSelection || 'all';
      if (formData.tenantApartmentSelection === 'selected') {
        if (!formData.tenantSelectedApartments?.length) { showNote('Please select at least one apartment', 'error'); return; }
        params.selected_apartments = formData.tenantSelectedApartments;
      }
    }
    runExport('tenant', params, exportHelpers.exportTenantData);
  };

  const handleDownload = async (id) => {
    try {
      const r = await exportRequestAPI.download(id);
      if (r.success) { showNote('File downloaded successfully', 'success'); loadExportHistory(); }
      else showNote('Download failed', 'error');
    } catch { showNote('Download error', 'error'); }
  };

  const isApartmentValid = () => {
    if (formData.apartmentSelection === 'selected' && !formData.selectedApartments.length) return false;
    if (formData.apartmentPeriod === 'month'  && !formData.apartmentMonth) return false;
    if (formData.apartmentPeriod === 'custom' && (!formData.apartmentDateFrom || !formData.apartmentDateTo)) return false;
    return true;
  };
  const isCategoryValid = () => !!formData.categoryApartment && !!formData.categoryType;
  const isTenantValid   = () => {
    if (!formData.tenantDateFrom || !formData.tenantDateTo) return false;
    if (formData.tenantSelection === 'specific') return !!formData.selectedTenant;
    if (formData.tenantApartmentSelection === 'selected') return !!formData.tenantSelectedApartments?.length;
    return formData.tenantApartmentSelection === 'all';
  };

  // Shared sx for form labels
  const labelSx  = { color: palette.text.secondary };
  const radioSx  = { color: Colors.primary.main, '&.Mui-checked': { color: Colors.primary.main } };

  return (
    <Box sx={{ p: 3, backgroundColor: palette.background.primary, minHeight: '100vh' }}>
      <Grid container spacing={3}>

        {/* Apartment Export */}
        <Grid item xs={12} md={4}>
          <ExportCard title="Apartment Data" icon={<ApartmentIcon />} color={Colors.primary.main}
            onExport={handleApartmentExport} isExporting={exportStates.apartment.isExporting}
            progress={exportStates.apartment.progress} isDisabled={!isApartmentValid()}>
            <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
              <FormLabel component="legend" sx={labelSx}>Select Apartments</FormLabel>
              <RadioGroup value={formData.apartmentSelection} onChange={e => handleFormChange('apartmentSelection', e.target.value)} row>
                <FormControlLabel value="all"      control={<Radio sx={radioSx} />} label={<span style={labelSx}>All Apartments</span>} />
                <FormControlLabel value="selected" control={<Radio sx={radioSx} />} label={<span style={labelSx}>Selected Only</span>} />
              </RadioGroup>
              {formData.apartmentSelection === 'selected' && (
                <FormGroup sx={{ mt: 1, maxHeight: 120, overflow: 'auto' }}>
                  {loading.apartments ? <CircularProgress size={20} sx={{ color: Colors.primary.main }} /> :
                    apartments.map(apt => (
                      <FormControlLabel key={apt.id}
                        control={<Checkbox checked={formData.selectedApartments.includes(apt.id)} onChange={e => handleApartmentCheckbox(apt.id, e.target.checked)} sx={radioSx} />}
                        label={<span style={labelSx}>{apt.name}</span>} />
                    ))
                  }
                </FormGroup>
              )}
            </FormControl>

            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormControl component="fieldset" sx={{ mt: 1, mb: 1 }}>
                <FormLabel component="legend" sx={labelSx}>Export Based On</FormLabel>
                <FormGroup row>
                  {[['apartmentExportTenantsBased', true,  'Tenants Based'],
                    ['apartmentExportTenantsBased', false, 'Units Based']].map(([field, val, label]) => (
                    <FormControlLabel key={label}
                      control={<Checkbox checked={formData.apartmentExportTenantsBased === val} onChange={() => handleFormChange(field, val)} sx={radioSx} />}
                      label={<span style={labelSx}>{label}</span>} />
                  ))}
                </FormGroup>
              </FormControl>
              <FormLabel component="legend" sx={labelSx}>Data Period</FormLabel>
              <RadioGroup value={formData.apartmentPeriod} onChange={e => handleFormChange('apartmentPeriod', e.target.value)} row>
                {['current','month','custom'].map(v => (
                  <FormControlLabel key={v} value={v} control={<Radio sx={radioSx} />} label={<span style={labelSx}>{v.charAt(0).toUpperCase()+v.slice(1)}</span>} />
                ))}
              </RadioGroup>
              {formData.apartmentPeriod === 'month' && (
                <TextField type="month" value={formData.apartmentMonth} onChange={e => handleFormChange('apartmentMonth', e.target.value)} fullWidth sx={{ mt: 1 }} />
              )}
              {formData.apartmentPeriod === 'custom' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {[['From','apartmentDateFrom'],['To','apartmentDateTo']].map(([label,field]) => (
                    <Grid item xs={6} key={field}>
                      <TextField type="date" label={label} value={formData[field]} onChange={e => handleFormChange(field, e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </FormControl>
          </ExportCard>
        </Grid>

        {/* Category Export */}
        <Grid item xs={12} md={4}>
          <ExportCard title="Category Data" icon={<HomeIcon />} color={Colors.accent.blue}
            onExport={handleCategoryExport} isExporting={exportStates.category.isExporting}
            progress={exportStates.category.progress} isDisabled={!isCategoryValid()}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Apartment</InputLabel>
              <Select value={formData.categoryApartment} label="Select Apartment"
                onChange={e => handleFormChange('categoryApartment', e.target.value)} disabled={loading.apartments}>
                {apartments.map(apt => <MenuItem key={apt.id} value={apt.id}>{apt.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Category</InputLabel>
              <Select value={formData.categoryType} label="Select Category"
                onChange={e => handleFormChange('categoryType', e.target.value)}
                disabled={!formData.categoryApartment || loading.categories}>
                {categories.map(cat => <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormControl component="fieldset" sx={{ mt: 1, mb: 1 }}>
                <FormLabel component="legend" sx={labelSx}>Export Based On</FormLabel>
                <FormGroup row>
                  {[['categoryExportTenantsBased', true,  'Tenants Based'],
                    ['categoryExportTenantsBased', false, 'Units Based']].map(([field, val, label]) => (
                    <FormControlLabel key={label}
                      control={<Checkbox checked={formData.categoryExportTenantsBased === val} onChange={() => handleFormChange(field, val)} sx={radioSx} />}
                      label={<span style={labelSx}>{label}</span>} />
                  ))}
                </FormGroup>
              </FormControl>
              <FormLabel component="legend" sx={labelSx}>Data Period</FormLabel>
              <RadioGroup value={formData.categoryPeriod} onChange={e => handleFormChange('categoryPeriod', e.target.value)} row>
                {['current','month','custom'].map(v => (
                  <FormControlLabel key={v} value={v} control={<Radio sx={radioSx} />} label={<span style={labelSx}>{v.charAt(0).toUpperCase()+v.slice(1)}</span>} />
                ))}
              </RadioGroup>
              {formData.categoryPeriod === 'month' && (
                <TextField type="month" value={formData.categoryMonth} onChange={e => handleFormChange('categoryMonth', e.target.value)} fullWidth sx={{ mt: 1 }} />
              )}
              {formData.categoryPeriod === 'custom' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {[['From','categoryDateFrom'],['To','categoryDateTo']].map(([label,field]) => (
                    <Grid item xs={6} key={field}>
                      <TextField type="date" label={label} value={formData[field]} onChange={e => handleFormChange(field, e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </FormControl>
          </ExportCard>
        </Grid>

        {/* Tenant Export */}
        <Grid item xs={12} md={4}>
          <ExportCard title="Tenant Data" icon={<PersonIcon />} color={Colors.status.success}
            onExport={handleTenantExport} isExporting={exportStates.tenant.isExporting}
            progress={exportStates.tenant.progress} isDisabled={!isTenantValid()}>
            <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
              <FormLabel component="legend" sx={labelSx}>Select Tenants</FormLabel>
              <RadioGroup value={formData.tenantSelection} onChange={e => handleFormChange('tenantSelection', e.target.value)} row>
                <FormControlLabel value="all"      control={<Radio sx={radioSx} />} label={<span style={labelSx}>All Tenants</span>} />
                <FormControlLabel value="specific" control={<Radio sx={radioSx} />} label={<span style={labelSx}>Specific Tenant</span>} />
              </RadioGroup>
            </FormControl>

            {formData.tenantSelection === 'specific' && (
              <Autocomplete options={tenants} getOptionLabel={o => `${o.name} - ${o.unit_number}`}
                value={tenants.find(t => t.id === formData.selectedTenant) || null}
                onChange={(_, v) => { handleFormChange('selectedTenant', v?.id || ''); handleFormChange('tenantSearch', ''); }}
                loading={loading.tenants}
                filterOptions={(opts, { inputValue }) => opts.filter(o => o.name.toLowerCase().includes(inputValue.toLowerCase()) || o.unit_number.toLowerCase().includes(inputValue.toLowerCase()))}
                renderInput={params => (
                  <TextField {...params} label="Select Tenant" placeholder="Type to filter..." fullWidth sx={{ mb: 2 }}
                    InputProps={{ ...params.InputProps, endAdornment: (<>{loading.tenants ? <CircularProgress size={20} /> : null}{params.InputProps.endAdornment}</>) }} />
                )}
                renderOption={(props, o) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: palette.text.primary }}>{o.name}</Typography>
                      <Typography variant="caption" sx={{ color: palette.text.secondary }}>Unit: {o.unit_number}</Typography>
                    </Box>
                  </Box>
                )}
              />
            )}

            {formData.tenantSelection === 'all' && (
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend" sx={labelSx}>Select Apartments</FormLabel>
                <RadioGroup value={formData.tenantApartmentSelection || 'all'} onChange={e => handleFormChange('tenantApartmentSelection', e.target.value)} row>
                  <FormControlLabel value="all"      control={<Radio sx={radioSx} />} label={<span style={labelSx}>All Apartments</span>} />
                  <FormControlLabel value="selected" control={<Radio sx={radioSx} />} label={<span style={labelSx}>Selected Only</span>} />
                </RadioGroup>
                {formData.tenantApartmentSelection === 'selected' && (
                  <FormGroup sx={{ mt: 1, maxHeight: 120, overflow: 'auto' }}>
                    {loading.apartments ? <CircularProgress size={20} sx={{ color: Colors.primary.main }} /> :
                      apartments.map(apt => (
                        <FormControlLabel key={apt.id}
                          control={<Checkbox checked={formData.tenantSelectedApartments?.includes(apt.id) || false}
                            onChange={e => handleFormChange('tenantSelectedApartments', e.target.checked ? [...(formData.tenantSelectedApartments||[]),apt.id] : (formData.tenantSelectedApartments||[]).filter(x=>x!==apt.id))}
                            sx={radioSx} />}
                          label={<span style={labelSx}>{apt.name}</span>} />
                      ))
                    }
                  </FormGroup>
                )}
              </FormControl>
            )}

            <Grid container spacing={2}>
              {[['From Date','tenantDateFrom'],['To Date','tenantDateTo']].map(([label,field]) => (
                <Grid item xs={6} key={field}>
                  <TextField type="date" label={label} value={formData[field]} onChange={e => handleFormChange(field, e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                </Grid>
              ))}
            </Grid>
          </ExportCard>
        </Grid>

        {/* Export History */}
        <Grid item xs={12}>
          <ExportHistory history={exportHistory} onDownload={handleDownload} onRefresh={loadExportHistory} loading={loading.history} />
        </Grid>
      </Grid>

      <ProgressDialog open={progressDialog.open} onClose={() => setProgressDialog(p => ({ ...p, open: false }))}
        exportType={progressDialog.type} progress={progressDialog.progress} status={progressDialog.status} error={progressDialog.error} />

      <Snackbar open={notification.open} autoHideDuration={6000}
        onClose={() => setNotification(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setNotification(p => ({ ...p, open: false }))} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExportCenter;